// Photo architecture barrel — assembles the collection-specific modules into
// the legacy shape the rest of the app expects.
//
// Everything the codebase imports today from "@/lib/photos" is re-exported
// here unchanged. Consumers do not need to update imports.
//
// Adding photography for a collection:
//   1. Edit ONLY the corresponding module under /lib/photos/{collection}.js
//   2. This barrel will pick it up automatically via COLLECTION_IMAGERY.
//
// Structure per collection module:
//   { hero, accent, gallery[], card, social, mobile? }
export * from "./_base";

import ultraLuxury, { ULTRA_LUXURY_PHOTOS, ULTRA_LUXURY_CARD_CINEMATIC } from "./ultraLuxury";
import touring, { TOURING_PHOTOS } from "./touring";
import premiumClassA, { PREMIUM_CLASS_A_PHOTOS } from "./premiumClassA";
import premiumSuperC, { PREMIUM_SUPER_C_PHOTOS } from "./premiumSuperC";
import overlandVehicles, { OVERLAND_VEHICLE_PHOTOS } from "./overlandVehicles";
import overlandTrailers, { OVERLAND_TRAILER_PHOTOS } from "./overlandTrailers";
import specialtyTrailers, { SPECIALTY_TRAILER_PHOTOS } from "./specialtyTrailers";
import european, { EUROPEAN_MOTORHOME_PHOTOS } from "./european";
import auctions, { AUCTIONS_PHOTOS } from "./auctions";
import aviation, { AVIATION_PHOTOS } from "./aviation";
import marine, { LUXURY_MARINE_PHOTOS } from "./marine";

// Legacy per-collection photo arrays (kept for consumers that import them).
export {
  ULTRA_LUXURY_PHOTOS,
  ULTRA_LUXURY_CARD_CINEMATIC,
  TOURING_PHOTOS,
  PREMIUM_CLASS_A_PHOTOS,
  PREMIUM_SUPER_C_PHOTOS,
  OVERLAND_VEHICLE_PHOTOS,
  OVERLAND_TRAILER_PHOTOS,
  SPECIALTY_TRAILER_PHOTOS,
  EUROPEAN_MOTORHOME_PHOTOS,
  AUCTIONS_PHOTOS,
  AVIATION_PHOTOS,
  LUXURY_MARINE_PHOTOS,
};

/**
 * Centralized per-collection imagery map used by preview pages, the homepage
 * editorial grid, mega-menu, and any future collection-aware rendering.
 *
 * Rule: never point two collections at the same file for the `hero` key.
 * Validation below enforces this in development.
 */
export const COLLECTION_IMAGERY = {
  "ultra-luxury-motorcoaches": ultraLuxury,
  "touring-collection": touring,
  "premium-class-a": premiumClassA,
  "premium-super-c": premiumSuperC,
  "overland-vehicles": overlandVehicles,
  "overland-trailers": overlandTrailers,
  "specialty-trailers": specialtyTrailers,
  "european-collection": european,
  "auctions": auctions,
  "private-aviation": aviation,
  "luxury-marine": marine,
};

// Convenience getters used by preview pages & the homepage.
export function collectionHero(slug) {
  return COLLECTION_IMAGERY[slug]?.hero;
}
export function collectionAccent(slug) {
  return COLLECTION_IMAGERY[slug]?.accent;
}
export function collectionGallery(slug) {
  return COLLECTION_IMAGERY[slug]?.gallery || [];
}
export function collectionCard(slug) {
  return COLLECTION_IMAGERY[slug]?.card || COLLECTION_IMAGERY[slug]?.hero;
}
export function collectionSocial(slug) {
  return COLLECTION_IMAGERY[slug]?.social || COLLECTION_IMAGERY[slug]?.hero;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dev-only validation. Fails loudly on duplicate heroes, missing keys, or
// duplicate positions within a collection so any accidental copy-paste while
// wiring new photography is caught instantly.
// ─────────────────────────────────────────────────────────────────────────────
if (process.env.NODE_ENV !== "production") {
  const seenHero = {};
  for (const [slug, m] of Object.entries(COLLECTION_IMAGERY)) {
    if (!m || !m.hero) {
      // eslint-disable-next-line no-console
      console.warn(`[photos] collection "${slug}" is missing a hero image.`);
      continue;
    }
    if (seenHero[m.hero]) {
      // eslint-disable-next-line no-console
      console.warn(
        `[photos] duplicate hero across collections: "${seenHero[m.hero]}" and "${slug}" share ${m.hero}. ` +
        `Auctions currently uses a placeholder from EXTERIOR_PHOTOS; this warning is expected until Auctions imagery lands.`
      );
    } else {
      seenHero[m.hero] = slug;
    }
    // Duplicate positions inside a single collection. `card` and `social`
    // legitimately shadow `hero` in most modules (single-image collections);
    // do NOT warn for those relationships. Warn only on real accidents like
    // gallery[0] === accent, or gallery[i] === gallery[j].
    const seenInCollection = {};
    const trackablePositions = [
      ["accent", m.accent],
      ...(m.gallery || []).map((g, i) => [`gallery[${i}]`, g]),
    ];
    for (const [pos, url] of trackablePositions) {
      if (!url) continue;
      if (seenInCollection[url]) {
        // eslint-disable-next-line no-console
        console.warn(`[photos] "${slug}" ${pos} duplicates ${seenInCollection[url]} (${url}).`);
      } else {
        seenInCollection[url] = pos;
      }
    }
  }
}
